import { AutocompleteComponent, IAutocompleteOptions } from '../classes/autocomplete';

// OpportunityIndexSearch.ts
interface SchoolRecord {
    schoolID?: number | string;
    schoolName?: string;
    totalIndicatorScore?: number | string;
    opportunityIndexScore?: number | string;
    schoolProfileLink?: string;
}

type HeaderMap = Record<string, keyof SchoolRecord>;

export class OpportunityIndexSearch {

    private readonly mainBlock: HTMLElement;
    private resultCotainer: HTMLElement;
    private errorBlock: HTMLElement;
    private loadingSpinner: HTMLElement;
    // private readonly datalist: HTMLDataListElement;
    private dataElements = new Map<string, HTMLElement>();
    private elementClasses;
    constructor(
        rootSelector = "#opportunity-index-search-block",
    ) {
        this.mainBlock = document.querySelector<HTMLElement>('.opportunityIndexBlock');
        this.resultCotainer = this.mainBlock.querySelector('.indexResultContainer');
        this.errorBlock = this.mainBlock.querySelector('.opportunityIndex-error-block');
        this.loadingSpinner = this.mainBlock.querySelector('.loading-spinner');


        this.elementClasses = {
            studentsWithDisabilities: '% of Students with Disabilities',
            simpsonDiversityIndex: 'Simpson Diversity Index',
            englishLearnerStudents: '% of English Learner Students',
            temporaryLivingSituation: '% of Students  in Temporary Living Situation',
            trachersRetained: '% of Teachers Retained',
            povertlyRate: '200% of Poverty Rate',
            chicagoHardshipIndex: 'Chicago Hardship Index',
            uninsured: '% Uninsured',
            communityLifeExpectancy: 'Community Life Expectancy',
            fundingIndexA: 'School Budget & Changes in School Budget',
            fundingIndexB: 'Capital and Outside Funding',
            schoolNameText: ''
        }

        Object.keys(this.elementClasses).forEach(className => {
            const element: HTMLElement = this.resultCotainer.querySelector(`.${className}`);
            if (element) { this.dataElements.set(className, element) }
        })
    }

    async init(): Promise<void> {
        const schools = await this.getData('schoolList');
        this.autocompleteOptions(schools);
    }

    private showLoading(): void {
        if (this.loadingSpinner) {
            this.loadingSpinner.classList.remove('hide');
        }

        const inputContainer = this.mainBlock.querySelector('.autocomplete-input-container');
        if (inputContainer) {
            inputContainer.classList.add('loading');
        }

        this.resultCotainer?.classList.remove('show');
        this.errorBlock?.classList.add('hide');
    }

    private hideLoading(): void {
        if (this.loadingSpinner) {
            this.loadingSpinner.classList.add('hide');
        }

        const inputContainer = this.mainBlock.querySelector('.autocomplete-input-container');
        if (inputContainer) {
            inputContainer.classList.remove('loading');
        }
    }

    private checkIfPercent(value: number): string {
        if (value !== null) {
            return `${value}%`
        }
        return '';
    }

    private showPercentile(value: number, value2: number, schoolGrade: string, content?: string): string {
        if (!content) {
            content = ` <span>This score is higher than <strong> $percentileValue </strong> of ${schoolGrade} schools </span>`
        }
        if (value === null) {
            return content = 'Data is not available for this school';
        }
        return content.replace('$percentileValue', this.checkIfPercent(this.checkIfFieldEmpty(value, value2)))
    }

    private checkIfFieldEmpty(value: number, badgeData: number) {
        return this.toPercentageOrNumber(value, 2)
    }

    private badgeContent(value: number, maxValue: number = 5) {
        if (value) {
            return `<span class="badge"><span class="from">${value}</span>/${maxValue}</span>`
        }
        return ''
    }

    private addTooltip(tooltipClass: string, tooltipText) {
        this.mainBlock.querySelector(`.${tooltipClass}`)?.setAttribute('data-tooltip-content', tooltipText);
    }

    private setElement(key: string, html: string) {
        const target = this.dataElements.get(`${key}`);
        if (!target) {
            throw new Error(`Element ${String(key)} not found in the registry or in the DOM.`);
        }
        target.innerHTML = html;
    }

    private clearAllStringData() {
        this.dataElements.forEach((el, key) => {
            if (!el.isConnected) {
                console.warn(`Skipped '${key}': element already removed from DOM`);
                return;
            }
            el.innerHTML = '';
        })
    }

    private clearAllData() {
        this.resultCotainer && this.resultCotainer.classList.remove('show');
        this.clearAllStringData();
        this.indexSumResData(0);
        this.avarageIndexBlockData(0);
        this.hideLoading();

        if (this.errorBlock) {
            !this.errorBlock.classList.contains('hide') && this.errorBlock.classList.add('hide');
        }
    }

    private indexSumResData(indexScore) {
        const indexSumRes = this.resultCotainer.querySelector('.opportunity-block-index-sum');

        const indexValue = indexSumRes.querySelector('.opportunity-chart-value span');
        indexValue.innerHTML = indexScore;

        const chartElement = indexSumRes.querySelector('.opportunity-chart-bar-average')
        chartElement['style']['left'] = this.RangedPercentage(indexScore);
    }

    private avarageIndexBlockData(averageScore) {
        const avarageIndexBlock = this.resultCotainer.querySelector('.avarage-block');
        const avarageIndexValue = avarageIndexBlock.querySelector('.opportunity-chart-value span');
        avarageIndexValue.innerHTML = `${averageScore.toFixed(2)}`;

        const avarageIndexchartElement = avarageIndexBlock.querySelector('.opportunity-chart-bar-average')
        avarageIndexchartElement['style']['left'] = this.RangedPercentage(averageScore.toFixed(2), 1, 5);
    }

    private pdfBtnData(urlAddr) {
        const pdfBtn: HTMLElement = this.mainBlock.querySelector('.printPDFbtn');

        if (pdfBtn && urlAddr) {
            pdfBtn.style.display = 'block';
            pdfBtn.setAttribute('href', urlAddr);
        } else {
            pdfBtn.style.display = 'none';
        }
    }
    private createStatisticHTML(title, percentileValue1, percentileValue2, schoolGrade, total = 5) {
        return `
        <div class="content-item-statistic">
            ${this.badgeContent(percentileValue2, total)}
        </div>
        <div class="content-item-desc">
          <p class="content-item-title"> ${title}</p>
          <p>
            ${this.showPercentile(percentileValue1, percentileValue2, schoolGrade)}
          </p>
        </div>
      `;
    }
    private autocompleteOptions(schoolsData) {
        const schoolSearchBlocks = [].slice.call(document.querySelectorAll('[data-school-search-opportunity]'))

        const onschoolSelect = async (schoolid) => {
            this.showLoading();

            try {
                const schoolData = await this.getData('schoolData', schoolid);
                console.log(schoolData);

                this.hideLoading();

                if (schoolData) {

                    let schoolGrade;
                    if (schoolData.gradeCategory === 'ES') {
                        schoolGrade = 'elementary'
                    } else if (schoolData.gradeCategory === 'HS') {
                        schoolGrade = 'high'
                    }

                    if (this.resultCotainer) {
                        this.indexSumResData(schoolData.indicatorSum);
                        this.avarageIndexBlockData(schoolData.averageScore)
                        this.setElement('schoolNameText', schoolData.schoolName)

                        this.setElement('studentsWithDisabilities', `${this.createStatisticHTML(
                            this.elementClasses['studentsWithDisabilities'],
                            schoolData.ofStudentsWithDisabilities1,
                            schoolData.ofStudentsWithDisabilities2,
                            schoolGrade
                        )}`)

                        this.setElement('simpsonDiversityIndex', `${this.createStatisticHTML(
                            this.elementClasses['simpsonDiversityIndex'],
                            schoolData.simpsonDiversityIndex1,
                            schoolData.simpsonDiversityIndex2,
                            schoolGrade
                        )}`)

                        this.setElement('englishLearnerStudents', `${this.createStatisticHTML(
                            this.elementClasses['englishLearnerStudents'],
                            schoolData.ofEnglishLearners1,
                            schoolData.ofEnglishLearners2,
                            schoolGrade
                        )}`)

                        this.setElement('temporaryLivingSituation', `${this.createStatisticHTML(
                            this.elementClasses['temporaryLivingSituation'],
                            schoolData.ofStudentsInTemporaryLivingSituationsStls1,
                            schoolData.ofStudentsInTemporaryLivingSituationsStls2,
                            schoolGrade
                        )}`)

                        this.setElement('trachersRetained', `${this.createStatisticHTML(
                            this.elementClasses['trachersRetained'],
                            schoolData.ofTeachersRetained1,
                            schoolData.ofTeachersRetained2,
                            schoolGrade
                        )}`)

                        this.setElement('povertlyRate', `${this.createStatisticHTML(
                            this.elementClasses['povertlyRate'],
                            schoolData._200PovertyRate1,
                            schoolData._200PovertyRate2,
                            schoolGrade
                        )}`)

                        this.setElement('chicagoHardshipIndex', `${this.createStatisticHTML(
                            this.elementClasses['chicagoHardshipIndex'],
                            schoolData.chicagoHardshipIndex1,
                            schoolData.chicagoHardshipIndex2,
                            schoolGrade
                        )}`)

                        this.setElement('uninsured', `${this.createStatisticHTML(
                            this.elementClasses['uninsured'],
                            schoolData.uninsured1,
                            schoolData.uninsured2,
                            schoolGrade
                        )}`)

                        this.setElement('communityLifeExpectancy', `${this.createStatisticHTML(
                            this.elementClasses['communityLifeExpectancy'],
                            schoolData.communityLifeExpectancy1,
                            schoolData.communityLifeExpectancy2,
                            schoolGrade
                        )}`)

                        this.setElement('fundingIndexA', `${this.createStatisticHTML(
                            this.elementClasses['fundingIndexA'],
                            schoolData.fundingIndexA1,
                            schoolData.fundingIndexA2,
                            schoolGrade
                        )}`)

                        this.setElement('fundingIndexB', `${this.createStatisticHTML(
                            this.elementClasses['fundingIndexB'],
                            schoolData.fundingIndexB1,
                            schoolData.fundingIndexB2,
                            schoolGrade
                        )}`)

                        this.resultCotainer.classList.add('show');
                        this.mainBlock.querySelector('.autocomplete-results-panel').classList.add('hide');
                    }
                } else {
                    if (this.errorBlock) {
                        this.errorBlock.classList.contains('hide') && this.errorBlock.classList.remove('hide');
                    }
                    if (this.resultCotainer) {
                        this.resultCotainer.classList.contains('show') && this.resultCotainer.classList.remove('show')
                    }
                }
            } catch (error) {
                this.hideLoading();
                console.error('Error loading school data:', error);

                if (this.errorBlock) {
                    this.errorBlock.classList.contains('hide') && this.errorBlock.classList.remove('hide');
                }
                if (this.resultCotainer) {
                    this.resultCotainer.classList.contains('show') && this.resultCotainer.classList.remove('show')
                }
            }
        }

        schoolSearchBlocks.forEach(block => {
            const blockID = block.getAttribute('data-school-search-opportunity');
            const blockInput = block.querySelector('input[disabled]');
            const blockPanelContainer = block.querySelector('[data-school-search-results-panel]') as HTMLElement;
            const blockPanel = block.querySelector('[data-school-search-results]') as HTMLElement;
            const panelClose = block.querySelector('[data-school-search-close]') as HTMLButtonElement;
            const blockForm = block.querySelector('form') as HTMLFormElement;
            panelClose.addEventListener('click', () => this.clearAllData())

            const schoolSearchBlockOptions: IAutocompleteOptions = {
                input: blockInput,
                panelContainer: blockPanelContainer,
                resultsContainer: blockPanel,
                blockContainer: block,
                form: blockForm,
                keys: ["SchoolLongName"],
                autocompleteSelector: `#Autocomplete_${blockID}`,
                autocompleteResultsSelector: `#Autocomplete_results_${blockID}`,
                resultFormat: (data, source) => {

                    const schoolName = data['key'] == 'SchoolLongName' ? data.match : data['value']['SchoolLongName'] as string;
                    source.innerHTML = this.generatePageLink(data['value'].SchoolID, schoolName, data['value']['SchoolLongName']);
                    const item = source.querySelector('.autocomplete-result-link');

                    item.addEventListener('click', (e) => {
                        e.preventDefault();
                        blockInput.value = data['value']['SchoolLongName'];
                        onschoolSelect(data['value'].SchoolID)
                    });
                    source.setAttribute('class', 'autocomplete-result');
                },
                sortFunction: (a, b) => {
                    const aSchool = a['value']['SchoolLongName'].toLowerCase();
                    const bSchool = b['value']['SchoolLongName'].toLowerCase();
                    const aStartsWith = a.match.indexOf('<') === 0;
                    const bStartsWith = b.match.indexOf('<') === 0;
                    if (!aStartsWith && bStartsWith) {
                        return 1;
                    }
                    else if (aStartsWith && !bStartsWith) {
                        return -1;
                    }
                    else {
                        return aSchool.localeCompare(bSchool);
                    }
                },
                submitCallback: () => {
                    const firstLink = blockPanel.querySelector('.autocomplete-result-link') as HTMLElement;
                    if (firstLink) {
                        firstLink.click();
                    }
                },
                dataSrc: schoolsData,
                cache: true,
                closeButton: panelClose,
                uniqueId: blockID
            }

            new AutocompleteComponent(schoolSearchBlockOptions);
        })
    }

    private generatePageLink(schoolId: string, schoolName: string, query: string) {
        return `<a href="#" data-school-id="${schoolId}" class="autocomplete-result-link" tabindex="-1" data-query="${query}">${schoolName}</span>`;
    }

    private getAdjustedPercentage(number, min = 11, max = 55, midpoint = 30) {
        if (number <= min) return "0%"; // Below range
        if (number >= max) return "100%"; // Above range

        // Adjust range so the midpoint is 50%
        const range = max - min;
        const shift = midpoint - min;

        return (((number - min) / range * 100) - (shift / range * 100) + 50) + "%";
    }

    private RangedPercentage(number, min = 11, max = 55) {
        if (number <= min) return "0%"; // Below range
        if (number >= max) return "100%"; // Above range

        return `${(number - min) * 100 / (max - min)}%`;
    }

    private async getData(type: 'schoolList' | 'schoolData', schoolId?) {
        let url;
        if (type == 'schoolList') {
            const term = '';
            url = `/api/schoolsearch/?term=${encodeURIComponent(term)}`
        } else {
            if (schoolId) {
                url = `/api/metric/opportunityindex?schoolid=${schoolId}`
            }
        }

        const res = await fetch(
            url,
            {
                method: 'GET',
                headers: { Accept: 'application/json' },
            }
        );

        if (!res.ok) {
            throw new Error(`School search failed: ${res.status} ${res.statusText}`);
        }

        return res.json();
    }

    private toPercentageOrNumber(
        value: any,
        decimals: number = 1
    ): number {
        if (value <= 1) {
            return Number((value * 100).toFixed(decimals));
        } else {
            return value.toFixed(decimals);
        }
    }
}
